import type { DsSourceDto } from '@ads/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Tabs } from '../components/Tabs';
import { PublishConfig } from './ConfigPublish';
import { UsageConfig } from './ConfigUsage';
import { TextField } from '../components/Field';
import * as Ic from '../components/icons';
import { Seg } from '../components/Seg';
import { Actions, useViewMeta } from '../components/Shell';
import { useDsStyle } from '../doc/Doc';
import { ApiError, api } from '../lib/api';
import { dateTime } from '../lib/format';
import { keys, useDesignSystem, useSession } from '../lib/queries';

/* Configurações · design system do cliente: de onde vem, qual versão
   está valendo, e o botão de sincronizar. */

const ROLE_SW: Array<[string, string]> = [
  ['primary', 'Primária'], ['accent', 'Acento'], ['highlight', 'Destaque'], ['ink', 'Texto'],
  ['surface', 'Superfície'], ['surface-brand', 'Superfície de marca'], ['line', 'Linha'],
];

const TABS = [['ds', 'Design system'], ['publicacao', 'Publicação'], ['uso', 'Uso']] as const;
type Tab = (typeof TABS)[number][0];

export function Config() {
  const [params, setParams] = useSearchParams();
  const aba = params.get('aba');
  const tab: Tab = aba === 'publicacao' || aba === 'uso' ? aba : 'ds';
  useViewMeta({ nav: 'config', crumb: ['Configurações', TABS.find(([k]) => k === tab)![1]], j: .05 });
  const tabs = <Tabs options={TABS} value={tab} onChange={v => setParams(v === 'ds' ? {} : { aba: v }, { replace: true })} label="Seções" />;
  return tab === 'ds' ? <DsConfig tabs={tabs} /> : tab === 'uso' ? <UsageConfig tabs={tabs} /> : <PublishConfig tabs={tabs} />;
}

function DsConfig({ tabs }: { tabs: ReactNode }) {
  const { me } = useSession();
  const qc = useQueryClient();
  const ds = useDesignSystem();
  useDsStyle(ds.data);
  const sources = useQuery({ queryKey: ['ds-sources', me.org.id], queryFn: () => api<DsSourceDto[]>(`/orgs/${me.org.id}/design-system/sources`) });
  const active = sources.data?.find(s => s.active);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string; details?: string[] } | null>(null);

  const sync = useMutation({
    mutationFn: () => api<{ status: 'created' | 'unchanged'; warnings: string[] }>(`/orgs/${me.org.id}/design-system/sync`, { method: 'POST' }),
    onSuccess: r => {
      setMsg({ tone: 'ok', text: r.status === 'created' ? 'Nova versão ingerida.' : 'Nada mudou na fonte — a versão atual continua valendo.', details: r.warnings });
      void qc.invalidateQueries({ queryKey: keys.ds(me.org.id) });
      void qc.invalidateQueries({ queryKey: ['ds-sources', me.org.id] });
    },
    onError: e => setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Falhou.', details: e instanceof ApiError && Array.isArray(e.details) ? e.details as string[] : undefined }),
  });

  const [kind, setKind] = useState<'github' | 's3'>('github');
  const [form, setForm] = useState({ repo: '', ref: 'main', path: '', bucket: '', prefix: '', endpoint: '', secretRef: '' });
  const add = useMutation({
    mutationFn: () => api<{ sync: { error?: { message: string; details?: string[] } } }>(`/orgs/${me.org.id}/design-system/sources`, {
      method: 'POST',
      body: kind === 'github'
        ? { kind, config: { repo: form.repo, ref: form.ref || 'main', path: form.path }, secretRef: form.secretRef || null }
        : { kind, config: { bucket: form.bucket, prefix: form.prefix, ...(form.endpoint ? { endpoint: form.endpoint } : {}) }, secretRef: form.secretRef || null },
    }),
    onSuccess: r => {
      if (r.sync.error) setMsg({ tone: 'err', text: `Fonte cadastrada, mas a ingestão falhou: ${r.sync.error.message}`, details: r.sync.error.details });
      else setMsg({ tone: 'ok', text: 'Fonte cadastrada e sincronizada.' });
      void qc.invalidateQueries({ queryKey: keys.ds(me.org.id) });
      void qc.invalidateQueries({ queryKey: ['ds-sources', me.org.id] });
    },
    onError: e => setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Falhou.', details: e instanceof ApiError && Array.isArray(e.details) ? (e.details as Array<{ path: string; message: string }>).map(d => `${d.path}: ${d.message}`) : undefined }),
  });
  const owner = me.org.role === 'owner';
  const d = ds.data;

  return (
    <section className="view on" data-v="config">
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Configurações</span><b>{me.org.name}</b></div>
          <h1>Design system</h1>
          <p className="lede">As peças do Studio saem com a cor, a fonte e o tom de voz da {me.org.name}. O ADS lê o design system de um repositório GitHub ou de um bucket S3, e guarda cada versão.</p>
        </div>
        <div className="tools">{tabs}</div>
      </div>

      <div className="grid2 config-grid">
        <div className="stack">
          <div className="card">
            <h3><Ic.Layers />Versão em uso</h3>
            {d ? (
              <>
                <p className="sub">{d.name} {d.version} · {d.tokenCount} tokens · hash {d.hash}</p>
                <div className="ds-preview" data-ds={d.hash}>
                  <div className="ds-logo">{d.logo.primary && <img src={d.logo.primary} alt="" />}<span>{d.name}</span></div>
                  <div className="ds-swatches">
                    {ROLE_SW.filter(([r]) => d.roles[r.replace(/-(\w)/g, (_, c: string) => c.toUpperCase())]).map(([r, l]) => (
                      <span key={r} className="ds-swatch"><i style={{ background: `var(--ds-role-${r})` }} /><em>{l}</em></span>
                    ))}
                  </div>
                  <div className="ds-type">
                    <span className="h" style={{ fontFamily: 'var(--ds-role-font-heading)' }}>Aa</span>
                    <span className="b" style={{ fontFamily: 'var(--ds-role-font-body)' }}>{d.fonts.map(f => f.family).join(' · ') || 'fontes do sistema'}</span>
                  </div>
                  {d.voice.tone.length > 0 && <p className="ds-voice"><b>Tom de voz:</b> {d.voice.tone.join(', ')}{d.voice.avoid.length ? <> · <b>evita</b> {d.voice.avoid.join(', ')}</> : null}</p>}
                </div>
              </>
            ) : <p className="sub">Nenhuma versão ingerida ainda.</p>}
          </div>
        </div>

        <div className="stack">
          <div className="card">
            <h3><Ic.Link />Fonte</h3>
            {active ? (
              <div className="kv stack">
                <div><b>Tipo</b><span>{active.kind === 'github' ? 'Repositório GitHub' : active.kind === 's3' ? 'Bucket S3' : 'Pasta do servidor (exemplo)'}</span></div>
                <div><b>Onde</b><span className="mono">{Object.values(active.config).filter(Boolean).join(' · ')}</span></div>
                {active.secretRef && <div><b>Credencial</b><span className="mono">variável {active.secretRef}</span></div>}
                <div><b>Última sincronização</b><span>{active.lastSyncAt ? dateTime(active.lastSyncAt) : 'nunca'}</span></div>
                {active.lastError && <div><b>Último erro</b><span className="err-text">{active.lastError.split('\n')[0]}</span></div>}
              </div>
            ) : <p className="sub">Nenhuma fonte cadastrada.</p>}
          </div>

          {owner && (
            <div className="card">
              <h3><Ic.Plus />Conectar outra fonte</h3>
              <p className="sub">A credencial nunca fica no banco: informe o NOME da variável de ambiente que guarda o token.</p>
              <div className="stack" style={{ gap: 16 }}>
                <Seg value={kind} options={[{ value: 'github', label: 'GitHub' }, { value: 's3', label: 'S3 · Garage · R2' }]} onChange={setKind} />
                {kind === 'github' ? (
                  <>
                    <TextField id="repo" label="Repositório" value={form.repo} placeholder="aion/design-systems" onChange={v => setForm(f => ({ ...f, repo: v }))} />
                    <div className="grid c2">
                      <TextField id="ref" label="Branch ou tag" value={form.ref} onChange={v => setForm(f => ({ ...f, ref: v }))} />
                      <TextField id="path" label="Pasta" opt="opcional" value={form.path} placeholder="clientes/aurora" onChange={v => setForm(f => ({ ...f, path: v }))} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid c2">
                      <TextField id="bucket" label="Bucket" value={form.bucket} onChange={v => setForm(f => ({ ...f, bucket: v }))} />
                      <TextField id="prefix" label="Pasta" opt="opcional" value={form.prefix} onChange={v => setForm(f => ({ ...f, prefix: v }))} />
                    </div>
                    <TextField id="endpoint" label="Endpoint" opt="se não for o do ADS" value={form.endpoint} placeholder="https://s3.seudominio.com" onChange={v => setForm(f => ({ ...f, endpoint: v }))} />
                  </>
                )}
                <TextField id="secret" label="Variável da credencial" opt="opcional" value={form.secretRef} placeholder={kind === 'github' ? 'DS_GITHUB_TOKEN_AURORA' : 'DS_S3_AURORA'} onChange={v => setForm(f => ({ ...f, secretRef: v.toUpperCase() }))} />
                <button className="btn-ghost" type="button" disabled={add.isPending} onClick={() => add.mutate()}>{add.isPending ? 'Conectando…' : 'Conectar e sincronizar'}</button>
              </div>
            </div>
          )}

          {msg && (
            <div className={`notice ${msg.tone === 'err' ? 'warn' : ''}`}>
              {msg.tone === 'err' ? <Ic.Warning /> : <Ic.Check />}
              <div><p>{msg.text}</p>{msg.details?.length ? <ul>{msg.details.slice(0, 8).map((x, i) => <li key={i}>{String(x)}</li>)}</ul> : null}</div>
            </div>
          )}
        </div>
      </div>

      <Actions>
        <button className="btn-primary" type="button" disabled={!active || sync.isPending} onClick={() => sync.mutate()}>
          {sync.isPending ? 'Sincronizando…' : 'Sincronizar agora'}<Ic.Refresh />
        </button>
      </Actions>
    </section>
  );
}
